import Vue from 'vue'
import Vuex from 'vuex'
import notificationApi from 'api-client/notification';
import approversApi from 'api-client/approvers';
import deeplinkApi from 'api-client/deeplinks';
import rolesApi from 'api-client/roles';
import versionsModule from './modules/versions';
import inAppAlertsModule from './modules/inAppAlerts';
import schools from './modules/schools';
import permissions from './modules/permissions';
import moment from 'moment';
import router from "@/router";

Vue.use(Vuex);

function clone(object) {
  if (!object) {
    return object;
  } else if (Array.isArray(object)) {
    return object.map(item => clone(item));
  } else if (typeof object === 'object') {
    const result = {};
    Object.entries(object).forEach(([key, value]) => {
      result[key] = clone(value);
    })
    return result;
  }
  return object;
}

export default new Vuex.Store({
  state: {
    isLoading: false,
    loadingCount: 0,
    selectedApp: {},
    isLoadingNotificationsTable: false,
    failedFetchNotifications: false,
    sendingForApproval: false,
    createMode: false,
    detailsMode: false,
    approvalMode: false,
    errorPage: false,
    notifications: [],
    notification: null,
    notificationSetupDetails: {},
    notificationMessageDetails: {},
    notificationAudienceAndScheduling: {},
    notificationInfo: {},
    notificationStatus: null,
    approvers: [],
    roles: [],
    totalUsers: 0,
    isCheckingAudiencePayload: false,
    alert: "",
    errorMessage: null,
    showAlert: false,
    deeplinks: [],
    deeplink: null
  },
  getters: {
    isLoading: state => state.isLoading,
    isLoadingNotificationsTable: state => state.isLoadingNotificationsTable,
    failedFetchNotifications: state => state.failedFetchNotifications,
    sendingForApproval: state => state.sendingForApproval,
    createMode: state => state.createMode,
    detailsMode: state => state.detailsMode,
    approvalMode: state => state.approvalMode,
    errorPage: state => state.errorPage,
    selectedApp: state => state.selectedApp,
    notificationList: state => state.notifications,
    currentNotification: state => state.notification,
    notificationSetupDetails: state => state.notificationSetupDetails,
    notificationMessageDetails: state => state.notificationMessageDetails,
    notificationAudienceAndScheduling: state => state.notificationAudienceAndScheduling,
    notificationInfo: state => state.notificationInfo,
    approvers: state => state.approvers,
    usersTotal: state => state.totalUsers,
    alert: state => state.alert,
    errorMessage: state => state.errorMessage,
    showAlert: state => state.showAlert,
    deepLinksList: state => state.deeplinks,
    currentDeeplink: state => state.deeplink,
    roles: state => state.roles
  },
  mutations: {
    setSelectedApp(state, app) {
      state.selectedApp = app;
    },
    setCreateMode(state, isCreateMode) {
      state.createMode = isCreateMode;
    },
    setDetailsMode(state, isDetailsMode) {
      state.detailsMode = isDetailsMode;
    },
    setApprovalMode(state, isApprovalMode) {
      state.approvalMode = isApprovalMode;
    },
    setErrorPage(state, isError) {
      state.errorPage = isError;
    },
    setIsLoading(state, isLoading) {
      if (isLoading) {
        state.loadingCount++;
      } else {
        state.loadingCount--;
      }

      state.isLoading = state.loadingCount > 0;
    },
    isLoadingNotificationsTable(state, isLoadingNotifications) {
      state.isLoadingNotificationsTable = isLoadingNotifications;
    },
    failedFetchNotifications(state, failedFetchNotifications) {
      state.failedFetchNotifications = failedFetchNotifications;
    },
    setSendingForApproval(state, isSendingForApproval) {
      state.sendingForApproval = isSendingForApproval;
    },
    setNotifications(state, notifications) {
      state.notifications = notifications;
    },
    setNotification(state, notification) {
      state.notification = notification;
    },
    setNotificationSetupDetails(state, data) {
      Vue.$log.debug('setting new notification details', data);
      if (!data || !state.notificationSetupDetails) {
        state.notificationSetupDetails = data;
      } else {
        state.notificationSetupDetails = Object.assign(state.notificationSetupDetails, data);
      }
    },
    setNotificationMessageDetails(state, data) {
      Vue.$log.debug('setting notification message details', data);
      if (!data || !state.notificationMessageDetails) {
        state.notificationMessageDetails = data;
      } else {
        state.notificationMessageDetails = Object.assign(state.notificationMessageDetails, data);
      }
      if (state.notificationMessageDetails && state.notificationMessageDetails.message.data.deeplink === null) {
        data.message.data.deeplink = "";
        data.message.data.type = "";
      }
    },
    setNotificationAudienceAndScheduling(state, data) {
      Vue.$log.debug('setting notification audience and scheduling', data);
      if (!data || !state.notificationAudienceAndScheduling) {
        state.notificationAudienceAndScheduling = data;
      } else {
        state.notificationAudienceAndScheduling = Object.assign(state.notificationAudienceAndScheduling, data);
      }
      if (state.notificationAudienceAndScheduling && !state.notificationAudienceAndScheduling.scheduling.isScheduled) {
        state.notificationAudienceAndScheduling.scheduling.payload = "";
      }
    },
    setNotificationInfo(state, data) {
      Vue.$log.debug('setting notification information', data);
      if (!data || !state.notificationInfo) {
        state.notificationInfo = data;
      } else {
        state.notificationInfo = Object.assign(state.notificationInfo, data);
      }
    },
    setApprovers(state, data) {
      Vue.$log.debug('setting approvers', data);
      state.approvers = data;
    },
    setRoles(state, data) {
      Vue.$log.debug('setting roles', data);
      state.roles = data;
    },
    setUsersTotal(state, data) {
      Vue.$log.debug("setting user's quantity", data);
      state.totalUsers = data;
    },
    setIsCheckingAudiencePayload(state, data) {
      state.isCheckingAudiencePayload = data;
    },
    setAlert(state, data) {
      switch (data) {
        case 'approval':
          state.alert = {message: 'Notification sent for approval', type: "success"};
          break;
        case 'approvalError':
          state.alert = {message: "ERROR: Notification could not be sent for approval.", type: "error"};
          break;
        case 'discard':
          state.alert = {message: 'Notification was discarded', type: "success"};
          break;
        case 'discardError':
          state.alert = {message: 'ERROR: Notification could not be discarded', type: "error"};
          break;
        case 'copy':
          state.alert = {message: 'Notification copied', type: "success"};
          break;
        case 'copyError':
          state.alert = {message: 'ERROR: Notification could not be copied', type: "error"};
          break;
        case 'draft':
          state.alert = {message: 'Notification saved as draft', type: "success"};
          break;
        case 'draftError':
          state.alert = {message: 'ERROR: Notification could not be saved as draft', type: "error"};
          break;
        case 'createError':
          state.alert = {message: 'ERROR: Notification could not be created', type: "error"}
          break;
        default:
          state.alert = {message: '', type: "info"};
      }
    },
    setErrorMessage(state, data) {
      state.errorMessage = data;
    },
    setShowAlert(state, data) {
      state.showAlert = data;
    },
    setDeeplinks(state, deeplinks) {
      state.deeplinks = deeplinks;
    },
    setDeeplink(state, deeplink) {
      state.deeplink = deeplink;
    },
  },
  actions: {
    commitNotificationDetails({commit}, notification) {
      commit('setNotification', notification);
      commit('setNotificationSetupDetails', {
        name: notification.name,
        method: notification.method,
        displayMethod: notification.displayMethod,
      });
      commit('setNotificationMessageDetails', {
        message: {
          title: notification.message.title,
          body: notification.message.body,
          htmlBody: notification.message.htmlBody,
          data: {
            deeplink: notification.message.data.deeplink,
            type: notification.message.data.type
          }
        }
      });
      commit('setNotificationAudienceAndScheduling', {
        audience: {
          type: notification.audience.type,
          roles: notification.audience.roles,
          emails: notification.audience.emails,
        },
        approverEmail: notification.approverEmail,
        scheduling: {
          isScheduled: notification.scheduling.isScheduled,
          payload: notification.scheduling.payload
        }
      });
      commit('setNotificationInfo', {
        status: notification.status,
        sentDatetime: notification.sentDatetime,
        approverRequestDate: notification.approverRequestDate,
        createdDatetime: notification.createdDatetime,
        rejectionReason: notification.rejectionReason,
      });
    },
    fetchNotifications({commit}) {
      Vue.$log.debug('fetching all notifications...');
      commit('isLoadingNotificationsTable', true);
      return notificationApi.fetchNotifications()
        .then(notifications => {
          commit('failedFetchNotifications', false);
          commit('setNotifications', notifications);
        })
        .catch(err => {
          Vue.$log.error(err);
          commit('failedFetchNotifications', true);
        })
        .finally(() => {
          commit('isLoadingNotificationsTable', false);
        })
    },
    fetchNotification({commit, dispatch}, id) {
      Vue.$log.debug('fetching notification with id: ', id);
      commit('isLoadingNotificationsTable', true);
      return notificationApi.fetchNotification(id)
        .then(notification => {
          if (notification) {
            dispatch('commitNotificationDetails', notification);
          }
          return notification;
        })
        .catch(err => {
          Vue.$log.error(err);
          // handling error in the caller
          throw err;
        })
        .finally(() => {
          commit('isLoadingNotificationsTable', false);
        });
    },
    createNotification({state, dispatch}) {
      Vue.$log.debug('creating a notification...');
      const notification = {
        name: state.notificationSetupDetails.name,
        method: state.notificationSetupDetails.method,
        displayMethod: state.notificationSetupDetails.displayMethod,
        message: {
          title: null,
          body: null,
          htmlBody: null,
          data: {
            deeplink: null,
            type: null
          }
        },
        scheduling: {
          isScheduled: null,
          payload: null,
        },
        creatorEmail: router.app.$OAuth.data.profile.email,
        creatorName: router.app.$OAuth.data.profile.givenName + " " + router.app.$OAuth.data.profile.sn,
        createdDatetime: moment(new Date()).format(),
        approverEmail: null,
        audience: {
          type: null,
          roles: [],
          emails: [],
        },
        status: "Draft",
        rejectionReason: null,
        draftPage: 1,
        approverRequestDate: null,
        sentDatetime: null,
      }

      switch (localStorage.lastSelectedApp) {
        case 'staffPortalApp': notification.appName = 'StaffPortalApp'; break;
        case 'qPlusApp': notification.appName = 'qplus'; break;
      }

      return notificationApi.createNotification(notification)
        .then(notification => {
          if (state.createMode) {
            dispatch('commitNotificationDetails', notification);
          } else if (!state.notifications.find(n => n.draftId === notification.draftId)) {
            dispatch('fetchNotifications');
          }
        })
        .catch(err => {
          Vue.$log.error(err);
          // handling error in the caller
          throw err;
        });
    },
    updateNotification({state, dispatch}, draftPage) {
      Vue.$log.debug('updating the current notification...');
      // current notification structure in the store
      let notification = clone(state.notification);
      // replace null values if any field has been updated with a truth value
      notification = Object.assign(notification, state.notificationSetupDetails);
      notification = Object.assign(notification, state.notificationMessageDetails);
      notification = Object.assign(notification, state.notificationAudienceAndScheduling);
      notification = Object.assign(notification, state.notificationInfo);
      notification.draftPage = draftPage;

      const payload = {
        id: state.notification.draftId,
        notification: notification
      };

      return notificationApi.updateNotification(payload)
        .then(notification => {
          if (state.createMode) {
            dispatch('commitNotificationDetails', notification);
          }
        })
        .catch(err => {
          Vue.$log.error(err);
          // handling error in the caller
          throw err;
        });
    },
    deleteNotification({state, commit, dispatch}, id) {
      commit('isLoadingNotificationsTable', true);
      notificationApi.deleteNotification(id)
        .then(() => {
          if (!state.createMode) {
            dispatch('fetchNotifications')
              .finally(() => {
                commit('setAlert', 'discard');
              })
          } else {
            commit('setAlert', 'discard');
          }
        })
        .catch(err => {
          Vue.$log.error(err);
          commit('setAlert', 'discardError');
        })
        .finally(() => {
          commit('isLoadingNotificationsTable', false);
          commit('setShowAlert', true);
          setTimeout(() => {
            commit('setShowAlert', false);
            commit('setAlert', '');
          }, 2000);
        })
    },
    copyNotification({commit, dispatch}, notification) {
      notification = clone(notification);
      const copyData = {
        name: notification.name + ' copy',
        status: "Draft",
        draftPage: 4,
        createdDatetime: moment(new Date()).format(),
        approverRequestDate: null,
        sentDatetime: null,
        rejectionReason: null
      }
      if (notification.scheduling.isScheduled &&
        Date.parse(notification.scheduling.payload) < new Date()) {
        copyData["scheduling"] = {
          isScheduled: false,
          payload: null
        }
      }
      notification = Object.assign(notification, copyData);
      commit('isLoadingNotificationsTable', true);
      notificationApi.createNotification(notification)
        .then(() => {
          commit('setAlert', 'copy');
          dispatch('fetchNotifications');
        })
        .catch(err => {
          Vue.$log.error(err);
          commit('setAlert', 'copyError');
        })
        .finally(() => {
          commit('setShowAlert', true);
          setTimeout(() => {
            commit('setShowAlert', false);
            commit('setAlert', '');
          }, 2000);
          commit('isLoadingNotificationsTable', false);
        })
    },
    sendForApproval({commit, dispatch}) {
      commit('setNotificationInfo', {
        status: 'Pending',
        rejectionReason: null,
      });
      commit('setSendingForApproval', true);
      return dispatch('updateNotification', 4)
        .then(() => {
          commit('setAlert', 'approval');
        })
        .catch(err => {
          Vue.$log.error(err);
          commit('setAlert', 'approvalError');
        })
        .finally(() => {
          commit('setSendingForApproval', false);
          commit('setShowAlert', true);
          setTimeout(() => {
            commit('setShowAlert', false);
            commit('setAlert', '');
          }, 2000);
        })
    },
    approveNotification({state, commit, dispatch}) {
      if (state.notification.scheduling.isScheduled) {
        // check if scheduled date has passed
        if (Date.parse(state.notification.scheduling.payload) < new Date()) {
          commit('setNotificationAudienceAndScheduling', {
            scheduling: {
              isScheduled: false,
              payload: null
            }
          });
        }
        return dispatch('updateNotification', 4)
          .then(() => {
            return notificationApi.approveNotification(state.notification.draftId)
              .then(() => {
                commit('setNotification', null);
                commit('setNotificationSetupDetails', null);
                commit('setNotificationMessageDetails', null);
                commit('setNotificationAudienceAndScheduling', null);
                commit('setNotificationInfo', null);
              })
              .catch(err => {
                // handle error in NotificationsApproval.vue
                throw err;
              });
          })
      } else {
        return notificationApi.approveNotification(state.notification.draftId)
          .then(() => {
            commit('setNotification', null);
            commit('setNotificationSetupDetails', null);
            commit('setNotificationMessageDetails', null);
            commit('setNotificationAudienceAndScheduling', null);
            commit('setNotificationInfo', null);
          })
          .catch(err => {
            // handle error in NotificationsApproval.vue
            throw err;
          });
      }
    },
    rejectNotification({commit, dispatch}, rejectionReason) {
      if (!rejectionReason) rejectionReason = null;
      commit('setNotificationInfo', {
        status: 'Rejected',
        rejectionReason: rejectionReason,
        approverRequestDate: null
      });
      return dispatch('updateNotification', 4)
        .then(() => {
          commit('setNotification', null);
          commit('setNotificationSetupDetails', null);
          commit('setNotificationMessageDetails', null);
          commit('setNotificationAudienceAndScheduling', null);
          commit('setNotificationInfo', null);
        })
        .catch(err => {
          // handle error in NotificationsApproval.vue
          throw err;
        })
    },
    fetchApprovers({commit}) {
      Vue.$log.debug('fetching approvers...');
      return approversApi.fetchApprovers()
        .then(approvers => commit('setApprovers', approvers))
        .catch(err => {
          Vue.$log.error(err);
          throw err;
        })
    },
    fetchQplusApprovers({commit}) {
      Vue.$log.debug('fetching qplus approvers...');
      return approversApi.fetchQplusApprovers()
        .then(approvers => commit('setApprovers', approvers))
        .catch(err => {
          Vue.$log.error(err);
          throw err;
        })
    },
    fetchStaffappApprovers({commit}) {
      Vue.$log.debug('fetching staffapp approvers...');
      return approversApi.fetchStaffappApprovers()
        .then(approvers => commit('setApprovers', approvers))
        .catch(err => {
          Vue.$log.error(err);
          throw err;
        })
    },
    fetchDeeplinks({commit}) {
      Vue.$log.debug('fetching deeplinks...');
      return deeplinkApi.fetchDeeplinks().then(deeplinks => commit('setDeeplinks', deeplinks.deeplinks));
    },
    fetchRoles({commit}, userId) {
      Vue.$log.debug('fetching roles...');
      const rolesArr = [];
      rolesApi.fetchRoles(userId).then(roles => {
        if (!Array.isArray(roles)) {
          rolesArr.push(roles);
          commit('setRoles', rolesArr);
        } else {
          commit('setRoles', roles);
        }
      });
    },
  },
  modules: {
    versionsModule,
    inAppAlertsModule,
    schools,
    permissions
  }
})
